import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import {
  Mutation,
  MutationAddVisitorArgs,
  MutationAddVisitorsToVisitorGroupArgs,
  MutationSetVisitorByIdArgs,
  Query,
  QueryGetVisitorByIdArgs,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import {
  ADD_VISITOR_WITH_ROLE,
  ADD_VISITORS_TO_VISITOR_GROUP,
  SET_VISITOR_BY_ID_WITH_ROLE
} from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_VISITOR_GROUPS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_DETAILS
} from 'graphql/v2/queries';
import { TranslatedHookProps } from 'hooks/shared/types';
import { useEffect } from 'react';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { refetchQueries } from 'v2/hooks/useAllVisitors/useAllVisitors';
import { updateVisitorGroupMembershipWithAdminGroup } from './utils';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_VISITOR_GROUPS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitorGroupsForTenant'>> =
  GET_ALL_VISITORS_VISITOR_GROUPS;
const GET_VISITOR_DETAILS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorById'>,
  QueryGetVisitorByIdArgs
> = GET_VISITOR_DETAILS;
const ADD_VISITOR_WITH_ROLE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addVisitor'>,
  MutationAddVisitorArgs
> = ADD_VISITOR_WITH_ROLE;
const SET_VISITOR_BY_ID_WITH_ROLE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'setVisitorById'>,
  MutationSetVisitorByIdArgs
> = SET_VISITOR_BY_ID_WITH_ROLE;
const ADD_VISITORS_TO_VISITOR_GROUP_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addVisitorsToVisitorGroup'>,
  MutationAddVisitorsToVisitorGroupArgs
> = ADD_VISITORS_TO_VISITOR_GROUP;

const useAddVisitor = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const {
    loading: coContractorsVisitorGroupsLoading,
    data: coContractorsVisitorGroupsData,
    error: coContractorsVisitorGroupsError
  } = useQuery(GET_ALL_VISITORS_VISITOR_GROUPS_TYPED, {
    variables: {
      filter: { type: VisitorGroupType.CoContractors }
    }
  });

  useEffect(() => {
    if (coContractorsVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(coContractorsVisitorGroupsError);
    }
  }, [coContractorsVisitorGroupsError]);

  const [fetchVisitorDetails, { loading: fetchVisitorDetailsLoading, error: fetchVisitorDetailsError }] =
    useLazyQuery(GET_VISITOR_DETAILS_TYPED);

  const [
    fetchAdministratorsVisitorGroups,
    {
      loading: administratorsVisitorGroupsLoading,
      data: administratorsVisitorGroupsData,
      error: administratorsVisitorGroupsError
    }
  ] = useLazyQuery(GET_ALL_VISITORS_VISITOR_GROUPS_TYPED);

  useEffect(() => {
    if (administratorsVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(administratorsVisitorGroupsError);
    }
  }, [administratorsVisitorGroupsError]);

  useEffect(() => {
    fetchAdministratorsVisitorGroups({ variables: { filter: { type: VisitorGroupType.Administrators } } });
  }, [fetchAdministratorsVisitorGroups]);

  const [
    fetchInstallersVisitorGroups,
    { loading: installersVisitorGroupsLoading, data: installersVisitorGroupsData, error: installersVisitorGroupsError }
  ] = useLazyQuery(GET_ALL_VISITORS_VISITOR_GROUPS_TYPED);

  useEffect(() => {
    if (installersVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(installersVisitorGroupsError);
    }
  }, [installersVisitorGroupsError]);

  useEffect(() => {
    fetchInstallersVisitorGroups({ variables: { filter: { type: VisitorGroupType.Installers } } });
  }, [fetchInstallersVisitorGroups]);

  const [addVisitorMutation, { loading: addVisitorLoading }] = useMutation(ADD_VISITOR_WITH_ROLE_TYPED, {
    refetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      if (error.message === 'Visitor already exists') handleFetchError(translation.user_email_already_exists);
      else if (error.message === 'Visitor is not only CO_CONTRACTOR')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_adding);
    }
  });

  const [updateVisitorMutation, { loading: updateVisitorLoading }] = useMutation(SET_VISITOR_BY_ID_WITH_ROLE_TYPED, {
    refetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      if (error.message === 'Visitor is not only CO_CONTRACTOR')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_updating);
    }
  });

  type AddOrEditVisitorArgs = {
    isNewVisitor: boolean;
    name: string;
    visitorGroupMembershipInput: VisitorGroupMembershipInput[];
    shouldBeAdmin: boolean;
    shouldBeInstaller: boolean;
    email?: string;
    id?: string;
    expirationDate?: dayjs.Dayjs;
  };

  const addOrEditVisitor = async ({
    isNewVisitor,
    name,
    shouldBeAdmin,
    shouldBeInstaller,
    email,
    id,
    expirationDate,
    visitorGroupMembershipInput
  }: AddOrEditVisitorArgs) => {
    let adminGroupData;
    let installerGroupData;
    try {
      [adminGroupData, installerGroupData] = await Promise.all([
        fetchAdministratorsVisitorGroups({ variables: { filter: { type: VisitorGroupType.Administrators } } }),
        fetchInstallersVisitorGroups({ variables: { filter: { type: VisitorGroupType.Installers } } }),
        id ? fetchVisitorDetails({ variables: { id } }) : undefined
      ]);
    } catch {
      handleFetchError(translation.external_groups_user_error_updating);
      return undefined;
    }
    let result;
    if (
      adminGroupData.data?.getVisitorGroupsForTenant === undefined ||
      installerGroupData.data?.getVisitorGroupsForTenant === undefined
    ) {
      handleFetchError(translation.external_groups_user_error_updating);
    }
    if (
      adminGroupData.data?.getVisitorGroupsForTenant !== undefined &&
      installerGroupData.data?.getVisitorGroupsForTenant !== undefined
    ) {
      const expirationDateIso = expirationDate ? dayjs(expirationDate).toISOString() : undefined;
      const membershipWithExpirationDate = expirationDateIso
        ? (visitorGroupMembershipInput || []).map((membership) => ({
            ...membership,
            expirationDate: expirationDateIso
          }))
        : visitorGroupMembershipInput || [];

      let updatedVisitorGroupMembership: VisitorGroupMembershipInput[] = updateVisitorGroupMembershipWithAdminGroup({
        visitorGroupMembership: membershipWithExpirationDate,
        options: {
          shouldBeAdmin,
          adminGroup: adminGroupData.data.getVisitorGroupsForTenant.find(
            (group) => group.type === VisitorGroupType.Administrators
          ),
          shouldBeInstaller,
          installerGroup: installerGroupData.data.getVisitorGroupsForTenant.find(
            (group) => group.type === VisitorGroupType.Installers
          )!
        }
      });

      try {
        if (isNewVisitor && email) {
          result = await addVisitorMutation({
            variables: {
              name,
              email,
              visitorGroupMembership: updatedVisitorGroupMembership
            },
            // Don't let a refetch failure reject the mutation promise — the visitor has
            // already been created on the backend at this point, and a rejected promise
            // would leave the drawer stuck open even though the add succeeded. Refetches
            // still fire (via refetchQueries above) to keep the list fresh in the background.
            awaitRefetchQueries: false
          });
        } else if (id) {
          result = await updateVisitorMutation({
            variables: {
              name,
              visitorId: id,
              visitorGroupMembership: updatedVisitorGroupMembership
            },
            refetchQueries: [...refetchQueries, { query: GET_VISITOR_DETAILS, variables: { id } }],
            awaitRefetchQueries: false
          });
        }
      } catch {
        // error is already surfaced via the mutations' onError above; we want to avoid an unhandled rejection
      }
    }
    return result;
  };

  const [addVisitorsToVisitorGroupMutation, { loading: addVisitorsToVisitorGroupLoading }] = useMutation(
    ADD_VISITORS_TO_VISITOR_GROUP_TYPED,
    {
      // The mutation result only returns the group's `members`, not its `memberIds`, those are two different fields and the member counts are based
      // on `memberIds`. Refetch the slim groups query so those counts include the newly added users, and the slim visitors
      // query so each added user's memberships are up to date.
      refetchQueries: [
        { query: GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS },
        { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }
      ],
      awaitRefetchQueries: true
    }
  );

  const addVisitorsToVisitorGroup = async ({ memberships, visitorGroupId }: MutationAddVisitorsToVisitorGroupArgs) => {
    const result = await addVisitorsToVisitorGroupMutation({
      variables: {
        memberships,
        visitorGroupId
      }
    });
    return result;
  };

  return {
    coContractors: coContractorsVisitorGroupsData?.getVisitorGroupsForTenant || [],
    coContractorsLoading: coContractorsVisitorGroupsLoading,
    administratorsVisitorGroups: administratorsVisitorGroupsData?.getVisitorGroupsForTenant,
    administratorsVisitorGroupsLoading: administratorsVisitorGroupsLoading,
    installersVisitorGroups: installersVisitorGroupsData?.getVisitorGroupsForTenant,
    installersVisitorGroupsLoading: installersVisitorGroupsLoading,
    addOrEditVisitor,
    addOrEditVisitorLoading:
      addVisitorLoading || updateVisitorLoading || administratorsVisitorGroupsLoading || installersVisitorGroupsLoading,
    addVisitorsToVisitorGroup,
    addVisitorsToVisitorGroupLoading
  };
};

export default useAddVisitor;
